import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Autocomplete as MuiAutocomplete, TextField } from '@mui/material';
import {
  GoogleMap,
  Autocomplete as PlacesAutocomplete,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader
} from '@react-google-maps/api';
import './AddTripModal.css'; // Reuse same CSS

import { useGetAllAssetsQuery, useGetAllLiteDevicesQuery, useGetAllUsersQuery } from '../../redux/admin/adminApi';
import { useGetClientsQuery, useGetMaterialsQuery } from '../../redux/logistics/logisticsApi';
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES } from '../../config/googleMaps';

const libraries = ['places', 'geometry', 'drawing','visualization'];

const mapContainerStyle = {
  width: '100%',
  height: '350px',
  borderRadius: '8px',
  marginTop: '10px'
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 };

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.assets)) return payload.assets;
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.clients)) return payload.clients;
  if (Array.isArray(payload.materials)) return payload.materials;
  return [];
};

const extractId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value?._id || value?.id || value?.$oid || '';
};

const safeLower = (value) => String(value || '').trim().toLowerCase();
const isMeaningfulVehicleName = (name, model) => {
  const normalizedName = safeLower(name);
  const normalizedModel = safeLower(model);
  if (!normalizedName) return false;
  if (normalizedName === normalizedModel) return false;
  return true;
};

const getVehicleLabel = (vehicle) =>
  vehicle?.vehicleNumber ||
  vehicle?.registrationNo ||
  (isMeaningfulVehicleName(vehicle?.name, vehicle?.model) ? vehicle?.name : '') ||
  (isMeaningfulVehicleName(vehicle?.vehicleName, vehicle?.model) ? vehicle?.vehicleName : '') ||
  vehicle?.deviceName ||
  vehicle?.IMEI ||
  vehicle?.imei ||
  vehicle?.model ||
  'Vehicle';

const extractMaterialValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value?._id || value?.name || value?.materialName || '';
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const tzOffset = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - tzOffset).toISOString().slice(0, 16);
};

const EditTripModal = ({ onClose, onSubmit, tripData }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const { data: usersResponse = [], isLoading: isUsersLoading } = useGetAllUsersQuery();
  const { data: assetResponse, isLoading: isVehicleLoading } = useGetAllAssetsQuery();
  const { data: devicesResponse = [] } = useGetAllLiteDevicesQuery();
  const { data: materialsResponse = [], isLoading: isMaterialsLoading } = useGetMaterialsQuery();

const { data: clientsData = [], isLoading: isClientsLoading } = useGetClientsQuery();
const clients = normalizeCollection(clientsData);
  const users = normalizeCollection(usersResponse);
  const drivers = Array.isArray(users) ? users.filter(u => String(u.role || "").toLowerCase() === "viewer") : [];
  const materials = normalizeCollection(materialsResponse);
  const assets = normalizeCollection(assetResponse);
  const devices = normalizeCollection(devicesResponse);
  const normalizedDevices = useMemo(
    () =>
      devices
        .filter((item) => item && String(item.status || '').toLowerCase() !== 'deleted')
        .map((item) => ({
          _id: item._id,
          IMEI: item.imei || item.IMEI || '',
          model: item.model || '',
          name: item.name || '',
          vehicleName: item.vehicleName || '',
          vehicleNumber: item.vehicleNumber || item.registrationNo || '',
          registrationNo: item.registrationNo || '',
        })),
    [devices]
  );

  const imeiToDeviceId = useMemo(
    () => new Map(normalizedDevices.map((item) => [safeLower(item.IMEI), item._id])),
    [normalizedDevices]
  );

  const selectedVehicleId = extractId(tripData?.vehicle);
  const selectedVehicleImei =
    tripData?.vehicle?.IMEI ||
    tripData?.vehicle?.imei ||
    tripData?.IMEI ||
    tripData?.imei ||
    '';
  const resolvedSelectedVehicleId = useMemo(
    () => imeiToDeviceId.get(safeLower(selectedVehicleImei)) || selectedVehicleId,
    [imeiToDeviceId, selectedVehicleImei, selectedVehicleId]
  );

  const vehicles = useMemo(() => {
    const normalizedAssets = assets
      .filter((item) => item && String(item.status || '').toLowerCase() !== 'deleted')
      .map((item) => {
        const assetImei = safeLower(item.IMEI || item.imei || '');
        const resolvedId = imeiToDeviceId.get(assetImei) || item._id;

        return {
          _id: resolvedId,
          IMEI: item.IMEI || item.imei || '',
          model: item.model || '',
          name: item.name || '',
          vehicleName: item.vehicleName || '',
          vehicleNumber: item.vehicleNumber || item.registrationNo || '',
          registrationNo: item.registrationNo || '',
        };
      });

    const merged = normalizedAssets.length
      ? [
          ...normalizedAssets,
          ...normalizedDevices.filter(
            (device) => !normalizedAssets.some((asset) => String(asset._id) === String(device._id))
          ),
        ]
      : normalizedDevices;

    const fallbackSelected =
      !merged.some((item) => String(item._id) === String(resolvedSelectedVehicleId)) && resolvedSelectedVehicleId
        ? [{
            _id: resolvedSelectedVehicleId,
            IMEI: selectedVehicleImei,
            model: tripData?.vehicle?.model || tripData?.model || '',
            vehicleNumber:
              tripData?.vehicle?.vehicleNumber ||
              tripData?.vehicle?.registrationNo ||
              tripData?.vehicleNumber ||
              tripData?.registrationNo ||
              '',
            registrationNo:
              tripData?.vehicle?.registrationNo ||
              tripData?.registrationNo ||
              '',
            name:
              tripData?.vehicle?.name ||
              tripData?.vehicle?.vehicleName ||
              tripData?.vehicleName ||
              '',
            vehicleName:
              tripData?.vehicle?.vehicleName ||
              tripData?.vehicleName ||
              '',
          }]
        : [];
    return [...fallbackSelected, ...merged];
  }, [assets, normalizedDevices, imeiToDeviceId, resolvedSelectedVehicleId, selectedVehicleImei, tripData]);

  const vehicleOptions = useMemo(
    () =>
      vehicles.map((vehicle) => ({
        ...vehicle,
        label: getVehicleLabel(vehicle),
        secondaryLabel: vehicle.IMEI || '',
        searchText: [
          vehicle.vehicleNumber,
          vehicle.registrationNo,
          vehicle.name,
          vehicle.vehicleName,
          vehicle.IMEI,
          vehicle.model,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [vehicles]
  );

  const [formData, setFormData] = useState({
    client: extractId(tripData?.client),
    vehicle: resolvedSelectedVehicleId,
    driver: extractId(tripData?.driver),
    material: extractMaterialValue(tripData?.material),
    load: tripData?.load || '',
    startAddr: tripData?.startAddr || '',
    endAddr: tripData?.endAddr || '',
    startDate: toDateInputValue(tripData?.startDate),
    endDate: toDateInputValue(tripData?.endDate),
    routeName: tripData?.routeName || '',
    distance: tripData?.distance || '',
    duration: tripData?.duration || ''
  });

  const [errors, setErrors] = useState({});
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [map, setMap] = useState(null);

  const selectedVehicleOption = useMemo(
    () => vehicleOptions.find((option) => String(option._id) === String(formData.vehicle)) || null,
    [formData.vehicle, vehicleOptions]
  );

  const originRef = useRef(null);
  const destRef = useRef(null);

  useEffect(() => {
    setFormData({
      client: extractId(tripData?.client),
      vehicle: resolvedSelectedVehicleId,
      driver: extractId(tripData?.driver),
      material: extractMaterialValue(tripData?.material),
      load: tripData?.load || '',
      startAddr: tripData?.startAddr || '',
      endAddr: tripData?.endAddr || '',
      startDate: toDateInputValue(tripData?.startDate),
      endDate: toDateInputValue(tripData?.endDate),
      routeName: tripData?.routeName || '',
      distance: tripData?.distance || '',
      duration: tripData?.duration || '',
    });
    setDirectionsResponse(null);
  }, [tripData, resolvedSelectedVehicleId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const directionsCallback = useCallback((response) => {
    if (response && response.status === 'OK') {
      setDirectionsResponse(response);

      const route = response.routes[0].legs[0];

      setFormData(prev => ({
        ...prev,
        distance: route.distance.text,
        duration: route.duration.text,
        startAddr: route.start_address,
        endAddr: route.end_address
      }));
    }
  }, []);

  const onPlaceSelected = () => {
    setDirectionsResponse(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.client) newErrors.client = 'Client is required';
    if (!formData.vehicle) newErrors.vehicle = 'Vehicle is required';
    if (!formData.driver) newErrors.driver = 'Driver is required';
    if (!formData.routeName) newErrors.routeName = 'Route name is required';
    if (!formData.startAddr) newErrors.startAddr = 'Start address is required';
    if (!formData.endAddr) newErrors.endAddr = 'End address is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      alert("Please fill all required fields");
      return;
    }

    onSubmit(tripData._id, formData);
  };

  if (!isLoaded) {
    return (
      <div className="atm-modal-overlay">
        <div className="atm-modal-content">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            🔄 Loading Map...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="atm-modal-overlay">
      <div className="atm-modal-content atm-modal-content--trip-edit">

        <div className="atm-modal-header">
          <h3>Edit Trip</h3>
          <button className="atm-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="atm-modal-body">

          {/* ROW 1: CLIENT, VEHICLE, DRIVER */}
          <div className="atm-form-grid-3">

            <div className="atm-form-group">
              <label>Client Name *</label>
              <select
                name="client"
                className={`atm-form-control ${errors.client ? 'atm-error' : ''}`}
                value={formData.client}
                onChange={handleChange}
              >
                <option value="">-- Select Client --</option>
                {isUsersLoading ? (
                  <option disabled>Loading clients...</option>
                ) : clients.length === 0 ? (
                  <option disabled>No clients available</option>
                ) : (
                  clients.map(c => (
                    <option key={c._id} value={c._id}>
                      {c.clientName || c.username || c.name} ({c.contactPersonPhone || c.mobileNo || c.email || "No contact"})
                    </option>
                  ))
                )}
              </select>
              {errors.client && <span className="atm-error-text">{errors.client}</span>}
            </div>

            <div className="atm-form-group">
              <label>Vehicle *</label>
              <MuiAutocomplete
                disablePortal
                options={vehicleOptions}
                loading={isVehicleLoading}
                value={selectedVehicleOption}
                onChange={(_, newValue) => {
                  setFormData((prev) => ({ ...prev, vehicle: newValue?._id || '' }));
                  if (errors.vehicle) {
                    setErrors((prev) => ({ ...prev, vehicle: '' }));
                  }
                }}
                isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
                getOptionLabel={(option) =>
                  option ? `${option.label}${option.secondaryLabel ? ` (${option.secondaryLabel})` : ''}` : ''
                }
                filterOptions={(options, state) => {
                  const query = String(state.inputValue || '').trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((option) => option.searchText.includes(query));
                }}
                noOptionsText={isVehicleLoading ? 'Loading vehicles...' : 'No vehicles found'}
                ListboxProps={{
                  style: { maxHeight: 280 },
                }}
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 1600,
                      '& .MuiAutocomplete-listbox': {
                        maxHeight: 280,
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select vehicle"
                    error={Boolean(errors.vehicle)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 44,
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                      <span style={{ fontWeight: 600 }}>{option.label}</span>
                      {option.secondaryLabel ? (
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{option.secondaryLabel}</span>
                      ) : null}
                    </div>
                  </li>
                )}
              />
              {errors.vehicle && <span className="atm-error-text">{errors.vehicle}</span>}
            </div>

            <div className="atm-form-group">
              <label>Driver *</label>
              <select
                name="driver"
                className={`atm-form-control ${errors.driver ? 'atm-error' : ''}`}
                value={formData.driver}
                onChange={handleChange}
              >
                <option value="">-- Select Driver --</option>
                {isUsersLoading ? (
                  <option disabled>Loading drivers...</option>
                ) : drivers.length === 0 ? (
                  <option disabled>No drivers available</option>
                ) : (
                  drivers.map(d => (
                    <option key={d._id} value={d._id}>
                    {d.username || d.name} ({d.mobileNo || d.email || d.uniqueId || "No contact"})
                    </option>
                  ))
                )}
              </select>
              {errors.driver && <span className="atm-error-text">{errors.driver}</span>}
            </div>

          </div>

          {/* ROW 2: MATERIAL, LOAD, DISTANCE */}
          <div className="atm-form-grid-3">
            <div className="atm-form-group">
              <label>Material</label>
              <select
                name="material"
                className="atm-form-control"
                value={formData.material}
                onChange={handleChange}
              >
                <option value="">-- Select Material --</option>
                {materials.map(m => (
                  <option key={m._id || m.name} value={m._id || m.name}>
                    {m.name} ({m.category})
                  </option>
                ))}
              </select>
            </div>

            <div className="atm-form-group">
              <label>Load (tons)</label>
              <input 
                type="number" 
                name="load" 
                className="atm-form-control" 
                value={formData.load}
                onChange={handleChange}
                placeholder="e.g., 5"
                min="0"
                step="0.1"
              />
            </div>

            <div className="atm-form-group">
              <label>Route Length / Duration</label>
              <input
                type="text"
                className="atm-form-control atm-readonly"
                readOnly
                value={formData.distance ? `${formData.distance} (${formData.duration})` : ''}
                placeholder="Auto-filled after route"
              />
            </div>
          </div>

          {/* ROW 3: DATES */}
          <div className="atm-form-grid-2">
            <div className="atm-form-group">
              <label>Start Date & Time *</label>
              <input
                type="datetime-local"
                name="startDate"
                className={`atm-form-control ${errors.startDate ? 'atm-error' : ''}`}
                value={formData.startDate}
                onChange={handleChange}
              />
              {errors.startDate && <span className="atm-error-text">{errors.startDate}</span>}
            </div>

            <div className="atm-form-group">
              <label>End Date & Time (Optional)</label>
              <input
                type="datetime-local"
                name="endDate"
                className="atm-form-control"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* ROUTE NAME */}
          <div className="atm-form-group">
            <label>Route Name *</label>
            <input
              type="text"
              name="routeName"
              className={`atm-form-control ${errors.routeName ? 'atm-error' : ''}`}
              value={formData.routeName}
              onChange={handleChange}
              placeholder="e.g., Delhi to Mumbai via NH48"
            />
            {errors.routeName && <span className="atm-error-text">{errors.routeName}</span>}
          </div>

          {/* ADDRESSES */}
          <div className="atm-form-grid-2">
            <div className="atm-form-group">
              <label>Start Address *</label>
              <PlacesAutocomplete onLoad={ref => originRef.current = ref} onPlaceChanged={onPlaceSelected}>
                <input
                  type="text"
                  name="startAddr"
                  className={`atm-form-control ${errors.startAddr ? 'atm-error' : ''}`}
                  value={formData.startAddr}
                  onChange={handleChange}
                  placeholder="Enter start location"
                />
              </PlacesAutocomplete>
              {errors.startAddr && <span className="atm-error-text">{errors.startAddr}</span>}
            </div>

            <div className="atm-form-group">
              <label>End Address *</label>
              <PlacesAutocomplete onLoad={ref => destRef.current = ref} onPlaceChanged={onPlaceSelected}>
                <input
                  type="text"
                  name="endAddr"
                  className={`atm-form-control ${errors.endAddr ? 'atm-error' : ''}`}
                  value={formData.endAddr}
                  onChange={handleChange}
                  placeholder="Enter destination"
                />
              </PlacesAutocomplete>
              {errors.endAddr && <span className="atm-error-text">{errors.endAddr}</span>}
            </div>
          </div>

          {/* MAP */}
          <div className="atm-map-wrapper">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={5}
              onLoad={(mapInstance) => setMap(mapInstance)}
            >
              {formData.startAddr && formData.endAddr && (
                  <DirectionsService
                    options={{
                      origin: formData.startAddr,
                      destination: formData.endAddr,
                      travelMode: 'DRIVING'
                    }}
                    callback={directionsCallback}
                  />
                )}

              {directionsResponse && (
                <DirectionsRenderer directions={directionsResponse} />
              )}
            </GoogleMap>
          </div>

        </div>

        {/* FOOTER */}
        <div className="atm-modal-footer">
          <button className="atm-btn-submit" onClick={handleSubmit}>
            UPDATE TRIP
          </button>
          <button className="atm-btn-cancel" onClick={onClose}>
            CANCEL
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditTripModal;
